import { EmptyState } from '@/components/feedback/EmptyState';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import { findBlockedWords, useBlockedWordsCheck } from '@/features/blocked-words/api';
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CheckSquare,
  Hash,
  ListMusic,
  Loader2,
  Megaphone,
  Send,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useTtsAccess } from '../api';
import { templateFeatures, type TtsFlowFormValues } from '../types';
import { StepBasics } from './steps/StepBasics';
import { StepReview } from './steps/StepReview';
import { StepTtsRows } from './steps/StepTtsRows';
import { discardDraftPrompts } from '../lib/draft-prompts';

interface Props {
  mode: 'create' | 'update' | 'clone';
  initialValues: TtsFlowFormValues;
  isPending: boolean;
  onSubmit: (values: TtsFlowFormValues) => void;
  /** Where to send the user when they click Back from the header. */
  backHref?: string;
}

const ALL_STEPS = [
  { id: 'basics', label: 'Basics', icon: Megaphone },
  { id: 'rows', label: 'TTS rows', icon: ListMusic },
  { id: 'review', label: 'Review', icon: CheckSquare },
] satisfies (StepDescriptor & { id: string })[];

type StepId = (typeof ALL_STEPS)[number]['id'];

/** Which steps apply for a given template. Template 7 (TTS Simple) skips
 *  the DTMF/voice-files step and the patch step. Template 8 skips patch. */
function visibleSteps(t: TtsFlowFormValues['templateId']): StepDescriptor[] {
  const f = templateFeatures(t);
  return ALL_STEPS.filter((s) => {
    if (s.id === 'voiceDtmf') return f.hasVoiceFiles;
    if (s.id === 'patch') return f.hasPatch;
    return true;
  });
}

function validateStepById(
  id: StepId,
  v: TtsFlowFormValues,
  blockedWords: string[] = [],
): string | null {

  if (id === 'basics') {
    if (!v.templateId) return 'Pick a template.';
    if (!v.ttsFlowName.trim()) return 'Give the flow a name.';
    if (!v.language) return 'Pick a language.';
    if (!v.gender) return 'Pick a voice gender.';
    return null;
  }

  if (id === 'rows') {
    if (v.ttsRows.length === 0) return 'Add at least one step — prompt, text, or variable.';
    for (let i = 0; i < v.ttsRows.length; i++) {
      const r = v.ttsRows[i]!;
      if (r.ttsType === 'variable') {
        if (!r.value) return `Step ${i + 1}: pick a variable slot.`;
        if (!r.variableType) return `Step ${i + 1}: pick a variable type.`;
        continue;
      }
      if (r.text !== undefined) {
        const text = r.text.trim();
        if (!text) return `Step ${i + 1}: type the text to speak.`;
        // Inline text limit (same as Add Voice File → Synthesize)
        if (text.length > 1000)
          return `Step ${i + 1}: text exceeds 1000 characters (currently ${text.length}).`;
        // ── Blocked word check on TTS text ──
        if (blockedWords.length > 0) {
          const matched = findBlockedWords(text, blockedWords);
          if (matched.length > 0)
            return `Step ${i + 1}: contains blocked word "${matched[0]}". Remove it to proceed.`;
        }
        continue;
      }
      if (!r.value) return `Step ${i + 1}: pick a prompt or switch to Text.`;
    }

    // ── Variable slots must be picked in sequence: 1, then 2, then 3…
    // Callers read slots in order at runtime, so gaps, duplicates and
    // random picks (1 + 3 with no 2) are all invalid. Sort + positional
    // check catches every case in one pass.
    const slots = v.ttsRows
      .filter((r) => r.ttsType === 'variable')
      .map((r) => Number(r.value))
      .filter((n) => Number.isInteger(n) && n > 0)
      .sort((a, b) => a - b);
    for (let i = 0; i < slots.length; i++) {
      if (slots[i] !== i + 1) {
        return `Variable slots must be picked in order — slot ${i + 1} comes first. You cannot select random slots.`;
      }
    }
    return null;
  }
  return null;
}

export function TtsFlowComposeWizard({
  mode,
  initialValues,
  isPending,
  onSubmit,
  backHref = '/tts-flow-list',
}: Props) {
  const [values, setValuesRaw] = useState<TtsFlowFormValues>(initialValues);
  const blockedWordsCheck = useBlockedWordsCheck();
    const ttsAccess = useTtsAccess();

  // Leaving the editor without saving → remove prompts uploaded for this flow.
  useEffect(() => () => discardDraftPrompts(), []);

  const setValues = (patch: Partial<TtsFlowFormValues>) =>
    setValuesRaw((prev) => ({ ...prev, ...patch }));

  const steps = useMemo(() => visibleSteps(values.templateId), [values.templateId]);
  const stepper = useStepper(steps.length);
  const currentId = (steps[stepper.current]?.id ?? 'basics') as StepId;

  const handleNext = () => {
    const err = validateStepById(currentId, values, blockedWordsCheck.data?.words ?? []);
    if (err) return toast.error(err);
    stepper.next();
  };

  const handleSubmit = () => {
    // Re-run every prior step's validator before submitting so jumping back
    // and forward via the stepper can't skip a check.
    for (let i = 0; i < steps.length - 1; i++) {
      const step = steps[i];
      if (!step) continue;
      const err = validateStepById(step.id as StepId, values, blockedWordsCheck.data?.words ?? []);
      if (err) {
        toast.error(err);
        stepper.goTo(i);
        return;
      }
    }
    onSubmit(values);
  };

  const stepBody = useMemo(() => {
    switch (currentId) {
      case 'basics':
        return (
          <StepBasics
            values={values}
            setValues={setValues}
            lockTemplate={mode === 'update'}
            autoFocusName={mode === 'create'}
          />
        );
      case 'rows':
        return <StepTtsRows values={values} setValues={setValues} />;
      case 'review':
        return <StepReview values={values} setValues={setValues} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, values, mode]);

  // No TTS module / no TTS balance → can't create TTS scripts (API blocks it too).
  if (ttsAccess.reason === 'no-module') {
    return (
      <ScrollPage>
        <EmptyState
          title="TTS flow can't be created"
          description={ttsAccess.message ?? undefined}
          action={
            <Button asChild variant="outline">
              <Link to={backHref}>Back</Link>
            </Button>
          }
        />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title={
          mode === 'create'
            ? 'Compose TTS Flow'
            : mode === 'clone'
              ? 'Clone TTS Flow'
              : 'Update TTS Flow'
        }
        description="Build a text-to-speech automation: pick prompts/variables, optionally collect DTMF and patch to agents, then schedule."
        actions={
          <Button asChild variant="outline">
            <Link to={backHref}>
              <ArrowLeft className="h-4 w-4" />
              Back to TTS flows
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={steps}
        current={stepper.current}
        completed={stepper.completed}
        onStepClick={stepper.goTo}
      />

      <div className="mt-6 space-y-4">{stepBody}</div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={stepper.prev} disabled={stepper.isFirst || isPending}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {stepper.isLast ? (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {mode === 'create' || mode === 'clone' ? 'Save TTS flow' : 'Save changes'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={isPending}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ScrollPage>
  );
}

// Suppress unused-import warning — kept for menu-icon parity with other wizards.
void Hash;
void CalendarClock;
